import { ChangeEvent, FC, MouseEvent, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import {
  DropdownWrapperMedium, InputWrapperLarge, InputWrapperMedium
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Button from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import { SubTitle } from '../../Classes/ClassForm/ClassForm.styles';
import { readOnlyLabelTableText } from '../../../constants/GlobalTexts';
import {
  EditForm, EditFormActions, EditFormFields, FormFieldRow, FormRadioGroupContainer
} from "../../Bases/EditForm.styles";
import { Size } from '../../../services/size/size.types';
import { useUpdateSize } from '../../../services/size/size.service';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { statusOptions } from "../../Bases/EditBase/StatusOptions";

interface EditSizeFormProps {
  size: Size;
  onSuccess: () => void;
}

interface EditSizeFormValues {
  status: string;
  displayDescription: string;
  sequence: string;
  displayInOrderEntry: boolean;
  validOnWeb: boolean;
}

const EditSizeForm: FC<EditSizeFormProps> = (
    {
      size,
      onSuccess
    }
) => {
  const sizeId = size.sizeNo;
  const {
    mutate: updateSize,
    isLoading: updateSizeIsLoading,
    isError: updateSizeIsError,
    error: updateSizeError
  } = useUpdateSize({ sizeId });

  useGenericErrorMessage([
    {
      error: updateSizeError,
      isError: updateSizeIsError
    }
  ]);

  const initialFormValues = useMemo<EditSizeFormValues>(() => ({
    status: size.statusNo?.toString(),
    displayDescription: size.displayDesc,
    sequence: size.seqNo?.toString(),
    displayInOrderEntry: size.displayInOe,
    validOnWeb: size.validOnWeb
  }), [size]);

  const [formValues, setFormValues] = useState<EditSizeFormValues>(initialFormValues);

  const [formErrors, setFormErrors] = useState({
    displayDescription: '',
    sequence: '',
    status: ''
  });

  const unsavedChanges = !isEqual(initialFormValues, formValues);

  const updateFormValues = (
      event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value
    });
  }

  const updateFormValuesFromRadioGroup = (
      event: ChangeEvent<HTMLInputElement>
  ) => {
    setFormErrors({
      ...formErrors,
      [event.target.name]: ''
    });
    setFormValues({
      ...formValues,
      [event.target.name]: event.target.value.includes('Yes')
    });
  }

  const handleSave = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    let error = false;
    let newFormErrors = {
      displayDescription: '',
      sequence: '',
      status: ''
    };

    if (formValues.displayDescription?.length > 9) {
      error = true;
      newFormErrors.displayDescription = 'BOH Description must be 9 characters or less';
    }

    if (!formValues.displayDescription || formValues.displayDescription.length < 1) {
      error = true;
      newFormErrors.displayDescription = 'Please enter a BOH Description';
    }

    if ([null, undefined, -1, ''].includes(formValues.sequence)) {
      error = true;
      newFormErrors.sequence = 'Sequence Number is required.';
    }

    if (formValues.sequence?.length > 3) {
      error = true;
      newFormErrors.sequence = 'Sequence Number must be 3 characters or less'
    }

    if (error) {
      setFormErrors(newFormErrors);
      return;
    }

    updateSize(
        {
          status: +formValues.status,
          displayDescription: formValues.displayDescription,
          sequence: +formValues.sequence,
          displayInOrderEntry: formValues.displayInOrderEntry,
          validOnWeb: formValues.validOnWeb
        },
        {
          onSuccess: onSuccess
        }
    );
  };

  return (
      <EditForm>
        <EditFormFields>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Size Number"}
                  id={"sizeNo"}
                  value={size.sizeNo.toString()}
                  disabled
              />
            </InputWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Short Description"}
                  id={"description"}
                  value={size.description}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperLarge>
              <Input
                  label={"Long Description"}
                  id={"longDescription"}
                  value={size.longDescription}
                  disabled
              />
            </InputWrapperLarge>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperLarge>
              <Input
                  label={"BOH Description"}
                  id={'displayDescription'}
                  name={'displayDescription'}
                  value={formValues.displayDescription}
                  onChange={updateFormValues}
                  errorText={formErrors.displayDescription}
              />
            </InputWrapperLarge>
            <DropdownWrapperMedium>
              <Dropdown
                  label={"Status"}
                  id={'status'}
                  name={'status'}
                  options={statusOptions}
                  value={formValues.status.toString()}
                  onChange={updateFormValues}
                  errorText={formErrors.status}
              />
            </DropdownWrapperMedium>
          </FormFieldRow>
          <FormFieldRow>
            <InputWrapperMedium>
              <Input
                  label={"Mnemonic"}
                  id={'mnemonic'}
                  value={size.mnemonic}
                  disabled
              />
            </InputWrapperMedium>
            <InputWrapperMedium>
              <MaskedInput
                  label={"Sequence Number"}
                  id={'sequence'}
                  name={'sequence'}
                  value={formValues.sequence.toString()}
                  isAllowed={(values) => +values.value < 2147483648}
                  onChange={updateFormValues}
                  onValueChange={(_values, source) => source.event && updateFormValues(source.event)}
                  errorText={formErrors.sequence}
              />
            </InputWrapperMedium>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Valid on Web?"}
                  id={'validOnWeb'}
                  options={[
                    {
                      label: 'Yes',
                      id: 'validOnWebYes'
                    },
                    {
                      label: 'No',
                      id: 'validOnWebNo'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.validOnWeb ? 'validOnWebYes' : 'validOnWebNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
            <FormRadioGroupContainer>
              <RadioGroup
                  label={"Valid in Order Entry?"}
                  id={'displayInOrderEntry'}
                  options={[
                    {
                      id: 'displayInOrderEntryYes',
                      label: 'Yes'
                    },
                    {
                      id: 'displayInOrderEntryNo',
                      label: 'No'
                    }
                  ]}
                  layout={RadioGroupLayouts.Row}
                  selected={formValues.displayInOrderEntry ? 'displayInOrderEntryYes' : 'displayInOrderEntryNo'}
                  onChange={updateFormValuesFromRadioGroup}
              />
            </FormRadioGroupContainer>
          </FormFieldRow>
          <SubTitle>{readOnlyLabelTableText}</SubTitle>
        </EditFormFields>
        <EditFormActions>
          <Button
              label={"Save"}
              onClick={handleSave}
              disabled={!unsavedChanges}
              isLoading={updateSizeIsLoading}
          />
        </EditFormActions>
        <UnsavedChangesModal unsavedChangesExist={unsavedChanges}/>
      </EditForm>
  );
};

export default EditSizeForm;